import type { ReactNode } from 'react';
import type { BlockView, CellView, DocumentView, RichBlockView, TextView } from '../../../api/documents';
import { Table } from '../../../components/idsk';
import type { RichInline, RichTextValue } from '../../../form-engine/richText';
import { RichTextView } from '../../../form-engine/react/RichTextView';

/** A level-2 heading of the document, used for the table of contents. */
export interface DocumentSection {
  id: string;
  text: TextView;
}

/** Ids of the level-2 headings (sections) in document order. */
export function documentSections(document: DocumentView, idPrefix: string): DocumentSection[] {
  return document.blocks
    .filter((b) => b.kind === 'HEADING' && b.level === 2 && b.text)
    .map((b, i) => ({ id: `${idPrefix}-section-${i + 1}`, text: b.text! }));
}

function Txt({ text }: { text: TextView }) {
  return text.lang ? <span lang={text.lang}>{text.value}</span> : <>{text.value}</>;
}

function richValue(block: RichBlockView): RichTextValue {
  const inline = (spans: RichBlockView['items'][number]): RichInline[] =>
    spans.map((s) => ({
      text: s.text,
      ...(s.bold ? { bold: true as const } : {}),
      ...(s.italic ? { italic: true as const } : {}),
      ...(s.underline ? { underline: true as const } : {}),
      ...(s.link ? { link: s.link } : {}),
    }));
  if (block.kind === 'PARAGRAPH') return { blocks: block.items.map((item) => ({ type: 'paragraph', content: inline(item) })) };
  return { blocks: [{ type: block.kind === 'BULLET_LIST' ? 'bulletList' : 'orderedList', items: block.items.map(inline) }] };
}

function CellContent({ cell }: { cell: CellView | undefined }) {
  if (!cell) return null;
  return (
    <>
      {cell.lines.map((line, i) => (
        <span className="app-document-line" key={i}>
          <Txt text={line} />
        </span>
      ))}
      {cell.rich.length > 0 && <RichTextView value={{ blocks: cell.rich.flatMap((b) => richValue(b).blocks) }} />}
      {cell.notes.map((note, i) => (
        <span className="app-document-note" key={`n${i}`}>
          <Txt text={note} />
        </span>
      ))}
    </>
  );
}

function headingClass(level: number): string {
  return level <= 2 ? 'govuk-heading-m' : 'govuk-heading-s';
}

/**
 * Renders the document model of a report or form document as ID-SK markup: the same blocks the PDF and DOCX
 * renderers use. The level-1 heading (the title) is left out – the page shows it as its own `h1`. Every table gets a
 * caption (the preceding heading, visually hidden, when the model has none).
 */
export function DocumentViewer({ document, idPrefix }: { document: DocumentView; idPrefix: string }) {
  const sections = documentSections(document, idPrefix);
  let section = 0;
  let lastHeading: TextView | null = null;
  const out: ReactNode[] = [];

  document.blocks.forEach((block: BlockView, i) => {
    if (block.kind === 'HEADING' && block.text) {
      const level = block.level ?? 2;
      if (level === 1) return;
      lastHeading = block.text;
      const Tag = `h${Math.min(6, level)}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const id = level === 2 ? sections[section++]?.id : undefined;
      out.push(
        <Tag key={i} id={id} tabIndex={id ? -1 : undefined} className={headingClass(level)}>
          <Txt text={block.text} />
        </Tag>,
      );
      return;
    }
    if (block.kind === 'PARAGRAPH' && block.text) {
      const className = block.style === 'LEAD' ? 'govuk-body-l' : block.style === 'NOTE' ? 'govuk-body-s app-document-note' : 'govuk-body';
      out.push(
        <p key={i} className={className}>
          <Txt text={block.text} />
        </p>,
      );
      return;
    }
    if (block.kind === 'TABLE' && block.table) {
      const table = block.table;
      const caption = table.caption ?? lastHeading;
      out.push(
        <Table<CellView[]>
          key={i}
          className="app-document-table"
          caption={caption ? <Txt text={caption} /> : ''}
          captionClassName={table.caption ? 'govuk-table__caption--s' : 'govuk-visually-hidden'}
          rows={table.rows}
          rowKey={(_, index) => String(index)}
          columns={table.headers.map((header, c) => ({
            key: String(c),
            header: <Txt text={header} />,
            rowHeader: c === 0 && table.rowHeaders,
            width: table.widths[c] ? `${table.widths[c]}%` : undefined,
            render: (row: CellView[]) => <CellContent cell={row[c]} />,
          }))}
        />,
      );
    }
  });

  return (
    <article className="app-document-view" lang={document.lang}>
      {out}
    </article>
  );
}
